import React, {memo, useCallback, useEffect, useMemo, useState} from "react";
import {useSelector, useDispatch} from 'react-redux';
import { Theme, useTheme } from "@mui/material/styles";
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import tagTextures, { defaultTagTexture, getTagTexturePath } from "../viewer/textures/tag-icons";
import Pagination from "@mui/lab/Pagination/Pagination";
import LinkIcon from '@mui/icons-material/Link';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import DeleteIcon from "@mui/icons-material/Delete";
import {
  createTags,
  updateTag,
  deleteTag,
  getTagAssets,
  selectAllAssets,
  TagData,
  TagItemData,
  Asset,
  createAsset,
  AssetType
} from "../redux/assets-slice";
import {createFolder, selectAllFolders} from '../redux/folders-slice';
import {
  DialogContent,
  InputLabel,
  TextField,
  Avatar,
  Grid,
  Collapse,
  Button,
  DialogActions,
  Select,
  FormHelperText
} from "@mui/material";
import clsx from 'clsx';
import {
  DraggableDialog,
  PromptDialog,
  EnhancedTable,
  SimpleDialog,
  InputWithLabel,
  DropdownWithLabel,
  DenseDivider,
  DividerWithText,
  EditableTextField,
  EditableDropdown,
  TextWithLabel,
  ColorPicker,
  optionsToDropdown,
  EditButtonGroup
} from "../components";
import { headerHeight, toast } from "../app";
import { getLocalizedFolderName, toLocaleString } from "../utilities";
import { useTranslation } from "react-i18next";
import { isStaticSite } from "../electron-modules";
import { nanoid } from "@reduxjs/toolkit";
import { useAuth, useDialog, useUniqueProjectID, useViewer } from "../hooks";
import { AnnotationDrawType, AnnotationSize, TagSize } from "../types/tags";
import { sendCustomEvent } from "../events";
import { useTableState } from "../hooks/use-table-state";
import { useAppDispatch } from "../redux/store";
import copyTextToClipboard from "copy-to-clipboard";
import { selectViewerSwitched } from "../redux/settings-slice";

export const tagIconsPerRow = 8;
export const tagRowsPerPage = 3;

const NEW_CSV_ID = "__new_csv__";
const NEW_FOLDER_ID = "__new_folder__";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    textField: {
      marginBottom: theme.spacing(3)
    },
    selectDivider: {
      margin: theme.spacing(0.5),
      opacity: 0.2
    },
    asterisk: {
      "&:after": {
        content:'"*"',
        color: "red",
        paddingLeft: theme.spacing(0.5)
      }
    },
    avatarParent: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      paddingTop: theme.spacing(2)
    },
    tagAvatar: {
      width: "24px",
      height: "24px",
      cursor: "pointer",
      "&:hover": {
        width: "26px",
        height: "26px",
        margin: "-1px"
      }
    },
    tagIconPagination: {
      paddingBottom: theme.spacing(2)
    },
    tagIconSearchParent: {
      textAlign: "right",
      paddingRight: theme.spacing(2),
      paddingBottom: theme.spacing(1)
    },
    tagIconPageParent: {
      width: "100%",
      display: "flex",
      flexDirection: "row-reverse"
    },
    tagIconParent: {
      position: "relative",
      display: "inline-block",
      width: `calc(${100/tagIconsPerRow}% - ${theme.spacing(2)})`,
      padding: theme.spacing(1)
    },
    tagIconCheck: {
      userSelect: "none",
      bottom: theme.spacing(1),
      right: theme.spacing(1),
      position: "absolute",
      color: "green",
      backgroundColor: "white",
      borderRadius: "50%"
    },
    tagIconImage: {
      width: `calc(100% - ${theme.spacing(1)})`,
      height: `calc(100% - ${theme.spacing(1)})`,
      margin: theme.spacing(0.5),
      cursor: "pointer",
      "&:hover": {
        width: "100%",
        height: "100%",
        margin: "0px",
      }
    },
    content: {
      padding: theme.spacing(0),
      paddingTop: "0px !important"
    }
  }),
);

interface TagIconDialogProps {
  open: boolean
  onSubmit(texture: string, size: number): void
  onClose(): void
  currentTexture: string
  currentSize: number
}

export const TagIconDialog = memo((props: TagIconDialogProps) => {
  const {open, onSubmit, onClose, currentTexture, currentSize} = props;

  const classes = useStyles();
  const {t} = useTranslation();

  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [tagTexture, setTagTexture] = useState("");
  const [tagSize, setTagSize] = useState(0);

  const handleIconClick = (texture) => {
    setTagTexture(texture);
  };

  const onSearch = (event) => {
    setPage(1);
    setSearch(event.target.value as string);
  };

  const onPageChange = (event, page: number) => {
    setPage(page);
  };

  const resetDefaults = () => {
    let selectedPage = Math.floor(
      textures.indexOf(currentTexture) / tagsPerPage) + 1;

    setSearch("");
    setTagTexture(currentTexture);
    setTagSize(currentSize);
    setPage(selectedPage);
  };

  useEffect(() => {
    if (!open) return;
    resetDefaults();
  }, [open]);

  const textures = useMemo(() => {
    const searchText = search.toLowerCase().trim();
    const searchTerms = searchText.split(" ");

    if (searchText === "") {
      return Object.keys(tagTextures);
    }

    return Object.keys(tagTextures)
      .map(key => {
        const itemText = key.toLowerCase();
        const matches = searchTerms.filter(x => itemText.includes(x)).length;
        return {key, matches};
      })
      .filter(x => x.matches >= searchTerms.length)
      .map(x => x.key);
  }, [search]);

  const iconChanged = (tagTexture !== currentTexture);
  const sizeChanged = (tagSize !== currentSize);

  const tagsPerPage = tagIconsPerRow * tagRowsPerPage;
  const numPages = Math.ceil(textures.length / tagsPerPage);

  const texturesForPage = textures.slice(
    (page-1) * tagsPerPage,
    page * tagsPerPage
  );

  return (
    <SimpleDialog
      open={open}
      onClose={onClose}
      title={t("tags.edit-change-icon")}
      canSubmit={iconChanged || sizeChanged}
      onSubmit={() => {
        onSubmit(tagTexture, tagSize);
      }}
      button={t("buttons.apply-changes")}
    >
      <div className={classes.tagIconSearchParent}>
        <TextField
          label={t("tags.edit-search-icon")}
          type="search"
          onChange={onSearch}
          value={search}
          size="small"
        />
      </div>

      <div>
        {texturesForPage.map(texture => (
          <div key={texture} className={classes.tagIconParent}>
            <img
              alt=""
              title={texture}
              className={classes.tagIconImage}
              src={getTagTexturePath(texture)}
              onClick={() => {
                handleIconClick(texture);
              }}
            />

            {(texture === tagTexture) && (<CheckCircleIcon
              fontSize="small"
              className={classes.tagIconCheck}
            />)}
          </div>
        ))}
      </div>

      <div className={classes.tagIconPageParent}>
        <Pagination
          page={page}
          count={numPages}
          onChange={onPageChange}
          className={classes.tagIconPagination}
        ></Pagination>
      </div>

      <DenseDivider/>

      <div style={{
        display: "flex",
        justifyContent: "center",
      }}>
        <Avatar
          src={getTagTexturePath(tagTexture)}
          variant="square"
          style={{
            margin: "auto 16px",
            zoom: 1 + (tagSize-1)/2.0,
          }}
        />

        {/** Icon Size */}
        <Grid item xs={6}>
          <DropdownWithLabel
            title={t('tags.icon_size')}
            label={t('tags.the_size_of_the_tag_icon')}
            value={tagSize}
            onChange={setTagSize}
            options={[
              [TagSize.XSmall, t('sizes.extra_small')],
              [TagSize.Small, t('sizes.small')],
              [TagSize.Medium, t('sizes.medium')],
              [TagSize.Large, t('sizes.large')],
              [TagSize.XLarge, t('sizes.extra_large')]
            ]}
          />
        </Grid>
      </div>

    </SimpleDialog>
  );
});

interface TagItemsDialogProps {
  asset: Asset
  open: boolean;
  title: string;
  onClose(): void;
}

export const TagItemsDialog = (props: TagItemsDialogProps) => {
  const {asset, open, title, onClose} = props;

  const {viewer} = useViewer();
  const {i18n, t} = useTranslation();

  const deleteDialog = useDialog();
  const {permissions} = useAuth();
  const tableState = useTableState({defaultSort: "name"});

  const formatter = viewer.getMeasurementFormatter();
  const projection = viewer.getViewProjection();
  const precision = formatter.getVectorPrecision(projection);

  const onRowClick = (row, index) => {
    if (index > 2) return;
    viewer.jumpToTag(row.tagID, true);
  };

  const getRows = (items) => {
    return items.map((tag, index) => {
      const isAerialTag = !("z" in tag);
      const x = tag.x.toFixed(precision.x);
      const y = tag.y.toFixed(precision.y);
      const z = isAerialTag ? "N/A" : tag.z.toFixed(precision.z);

      const coordinate = isAerialTag
        ? `(${x}, ${y})`
        : `(${x}, ${y}, ${z})`;

      const position = isAerialTag
        ? [tag.x, tag.y]
        : [tag.x, tag.y, tag.z];

      let rowData = {
        id: `TagTableRow_${tag.id}`,
        tagID: tag.id,
        tag: index + 1,
        modified: tag.date,
        name: tag.name,
        comment: tag.comment,
        coordinate,
        position
      };

      return rowData;
    });
  };

  const tagItems = (asset.data as TagData).items;

  const actions = useMemo(() => {
    const zoomToTag = {
      title: t('tags.move_to_tag'),
      icon: <ZoomInIcon fontSize="small"/>,
      onClick: (row) => {
        viewer.jumpToTag(row.tagID);
      }
    };

    const copyPosition = {
      title: t("tags.copy-to-clipboard"),
      icon: <FileCopyIcon fontSize="small"/>,
      onClick: (row) => {
        // Copy position to clipboard
        copyTextToClipboard(row.coordinate);
        toast.success(t("toast.copied-to-clipboard"));
      }
    };

    const copyURL = {
      title: t('tags.copy_tag_url'),
      icon: <LinkIcon fontSize="small"/>,
      onClick: (row) => {
        // Copy url to clipboard
        const url = new URL(window.location as any);
        url.hash = `tag=${row.tagID}`;
        copyTextToClipboard(url.href);
        toast.success(t("toast.copied-to-clipboard"));
      }
    };

    const deleteRow = {
      title: t("buttons.delete"),
      icon: <DeleteIcon fontSize="small"/>,
      onClick: (row) => {
        const tagToDelete = row.tagID;
        deleteDialog.handleOpen([tagToDelete]);
      }
    };

    let actions = [];
    actions.push(zoomToTag);
    actions.push(copyPosition);

    if (isStaticSite) {
      actions.push(copyURL);
    }

    if (permissions.delete) {
      actions.push(deleteRow);
    }

    return actions;
  }, [i18n.language, permissions, isStaticSite]);

  const columns = [
    {
      id: 'name',
      label: t("tags.name"),
      wordBreak: true,
      searchable: true
    },
    {
      id: 'comment',
      label: t("tags.comments"),
      wordBreak: true,
      searchable: true
    },
    {
      id: 'modified',
      label: t("tags.date"),
      date: true
    },
  ];

  const rows = useMemo(() => {
    return getRows(tagItems);
  }, [tagItems]);


  return (
    <React.Fragment>
      <DraggableDialog
        open={open}
        title={title}
        onClose={onClose}
      >
        <DialogContent>
          <EnhancedTable
            state={tableState}
            columns={columns}
            rows={rows}
            actions={actions}
            onClick={onRowClick}
          />
        </DialogContent>
      </DraggableDialog>

      {/* Delete Dialog */}
      <TagDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        toDelete={deleteDialog.data}
      />
    </React.Fragment>
  );
};

interface TagDeleteDialogProps {
  open: boolean;
  onClose(): void;
  onDelete?(): void;
  toDelete: string[];
}

export const TagDeleteDialog = (props: TagDeleteDialogProps) => {
  const {open, onClose, onDelete, toDelete = []} = props;

  const dispatch = useDispatch();
  const {t} = useTranslation();

  const onSubmit = () => {
    for (const tagID of toDelete) {
      dispatch(deleteTag(tagID));
    };

    onClose();
    onDelete?.();

    toast.success(t("toast.tag-removed", {
      count: numTagsToDelete
    }));
  };

  const numTagsToDelete = toDelete ? toDelete.length : 0;

  return (
    <PromptDialog
      open={open}
      onCancel={onClose}
      onSubmit={onSubmit}
      title={t("dialog.delete-tags")}
      prompt={t("toast.tag-remove-prompt", {
        count: numTagsToDelete
      })}
      button={t("buttons.delete")}
    />
  );
};

const getTagDetails = (assets: Asset[], tagID: string) => {
  const tagFiles = getTagAssets(assets);

  for (let file of tagFiles) {
    const tags = (file.data as TagData).items;
    const selected = tags.find(tag => tag.id === tagID);

    if (selected) {
      return {
        tag: selected,
        asset: file
      };
    };
  }

  return {
    tag: null,
    asset: null
  };
};

export const TagDetailsDialog = () => {
  const assets = useSelector(selectAllAssets);
  const uniqueProjectID = useUniqueProjectID();
  const detailsDialog = useDialog();
  const deleteDialog = useDialog();
  const {t} = useTranslation();
  const {permissions} = useAuth();

  const [tagID, setTagID] = useState(null);

  const onClose = () => {
    setTagID(null);
    detailsDialog.handleClose();
  };

  const onDelete = () => {
    deleteDialog.handleOpen([tagID]);
  };

  const {tag, asset} = useMemo(() => {
    return getTagDetails(assets, tagID);
  }, [assets, tagID]);

  useEffect(() => {
    /** Close dialog when new project is loaded */
    onClose();
  }, [uniqueProjectID]);

  /** Event listener */
  useEffect(() => {
    const callback = (event) => {
      setTagID(event.detail);
      detailsDialog.handleOpen();
    };

    document.addEventListener("tag-details", callback);

    return () => {
      document.removeEventListener("tag-details", callback);
    };
  }, []);

  return (
    <React.Fragment>
      <AnnotationsToolbar tag={tag}/>

      <DraggableDialog
        open={detailsDialog.open}
        resize={false}
        onClose={onClose}
        title={t("dialog.tag-details")}
      >
        <TagDetailsContent tag={tag} asset={asset}/>

        <DialogActions>
          {permissions.delete && <Button
            color="secondary"
            onClick={onDelete}
          >
            {t('buttons.delete')}
          </Button>}

          <Button
            disabled={false}
            onClick={onClose}
          >
            {t('buttons.close')}
          </Button>
        </DialogActions>

      </DraggableDialog>

      {/* Delete Dialog */}
      <TagDeleteDialog
        open={deleteDialog.open}
        onClose={deleteDialog.handleClose}
        onDelete={onClose}
        toDelete={deleteDialog.data}
      />
    </React.Fragment>
  );
};

interface AnnotationsToolbarProps {
  tag: TagItemData;
}

const AnnotationsToolbar = (props: AnnotationsToolbarProps) => {
  const {tag} = props;

  const theme = useTheme();
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const {viewer} = useViewer();
  const {permissions} = useAuth();

  const viewerSwitched = useSelector(selectViewerSwitched);

  const [editing, setEditing] = useState(false);
  const [size, setSize] = useState(AnnotationSize.Medium);
  const [type, setType] = useState(AnnotationDrawType.Polygon);
  const [color, setColor] = useState("#FF0000");

  const handleSave = () => {
    const annotations = viewer.getTagAnnotations();
    toast.success(t("toast.tag-details-update"));
    dispatch(updateTag({tagID: tag.id, annotations}));
  };

  const savedAnnotations = tag
    ? tag.annotations ? [...tag.annotations] : []
    : [];

  const handleCancel = () => {
    // Discard any changes and load saved annotations
    viewer.loadTagAnnotations(savedAnnotations);
  };

  useEffect(() => {
    if (!viewer) return;
    viewer.setTagAnnotationType(type);
  }, [viewer, type]);

  useEffect(() => {
    if (!viewer) return;
    viewer.setTagAnnotationColor(color);
  }, [viewer, color]);

  useEffect(() => {
    if (!viewer) return;
    viewer.setTagAnnotationSize(size);
  }, [viewer, size]);

  useEffect(() => {
    if (!viewer) return;

    if (tag) {
      viewer.toggleTagAnnotations(true);
      viewer.loadTagAnnotations(savedAnnotations);
      viewer.setTagSelected(tag.id);
    } else {
      viewer.toggleTagAnnotations(false);
      viewer.setTagSelected(null);
      setEditing(false);
    }
  }, [viewer, tag]);

  useEffect(() => {
    if (!viewer) return;
    viewer.setTagAnnotationDrawing(editing);
  }, [viewer, editing]);

  useEffect(() => {
    // Disable editing if we minimize the aerial view
    if (viewerSwitched) return;
    setEditing(false);
  }, [viewerSwitched]);

  const typeOptions = optionsToDropdown([
    [AnnotationDrawType.Polygon, t('tags.polygon')],
    [AnnotationDrawType.PolygonFreeHand, t('tags.polygon_freehand')],
    [AnnotationDrawType.LineString, t('tags.linestring')],
    [AnnotationDrawType.LineStringFreeHand, t('tags.linestring_freehand')],
    [AnnotationDrawType.Rectangle, t('tags.rectangle')],
    [AnnotationDrawType.Circle, t('tags.circle')],
    [AnnotationDrawType.Star, t('tags.star')],
    [AnnotationDrawType.Eraser, t('tags.eraser')],
  ]);

  const sizeOptions = optionsToDropdown([
    [AnnotationSize.Small, t('sizes.small')],
    [AnnotationSize.Medium, t('sizes.medium')],
    [AnnotationSize.Large, t('sizes.large')],
    [AnnotationSize.XLarge, t('sizes.extra_large')]
  ]);

  const showToolbar = tag
    && permissions.edit
    && viewerSwitched;

  return (
    <React.Fragment>
      {showToolbar && <div style={{
        width: "100%",
        position: "absolute",
        top: `${headerHeight}px`,
        paddingTop: theme.spacing(1),
        zIndex: theme.zIndex.modal,
        pointerEvents: "none"
      }}>
        <div style={{
          width: "fit-content",
          margin: "0 auto",
          pointerEvents: "all",
          background: "white",
          padding: theme.spacing(0.5, 1.5),
          borderRadius: theme.spacing(0.5),
          boxShadow: theme.shadows[16],
        }}>
          <FormHelperText>
            {t('tags.tag_annotations_toolbar')}
          </FormHelperText>

          <div style={{
            display: "flex",
            gap: theme.spacing(3),
          }}>
            {/** Annotation Type */}
            <Select
              value={type}
              disableUnderline
              disabled={!editing}
              onChange={event => {
                setType(event.target.value as AnnotationDrawType);
              }}
            >
              {typeOptions}
            </Select>

            {/** Annotation Size */}
            <Select
              value={size}
              disableUnderline
              disabled={!editing}
              onChange={event => {
                setSize(event.target.value as AnnotationSize);
              }}
            >
              {sizeOptions}
            </Select>

            {/** Annotation Color */}
            <ColorPicker
              value={color}
              onSubmit={setColor}
              disabled={!editing}
            />

            {/** Save/Edit Buttons */}
            <EditButtonGroup
              editing={editing}
              setEditing={setEditing}
              onSubmit={handleSave}
              onCancel={handleCancel}
            />
          </div>

        </div>
      </div>}
    </React.Fragment>
  );
};

interface TagDetailsContentProps {
  tag: TagItemData;
  asset: Asset;
}

const TagDetailsContent = (props: TagDetailsContentProps) => {
  const {tag, asset} = props;

  const dispatch = useDispatch();
  const assets = useSelector(selectAllAssets);
  const {i18n, t} = useTranslation();

  const onChangeName = (value: string) => {
    toast.success(t("toast.tag-details-update"));
    dispatch(updateTag({tagID: tag.id, name: value}));
  };

  const onChangeComment = (value: string) => {
    toast.success(t("toast.tag-details-update"));
    dispatch(updateTag({tagID: tag.id, comment: value}));
  };

  const onChangeAsset = (value: string) => {
    toast.success(t("toast.tag-details-update"));
    dispatch(updateTag({tagID: tag.id, assetID: value}));
  };

  const options = useMemo(() => {
    return getTagAssets(assets).map(file => {
      return [file.id, file.name];
    }) as [string, string][];
  }, [assets]);

  const dateString = tag
    ? toLocaleString(tag.date, i18n.language)
    : "";

  return (
    <React.Fragment>
      {tag && <DialogContent key={tag.id}>
        <Grid container spacing={2}>
          {/** Title */}
          <Grid item xs={12}>
            <EditableTextField
              title={t("tags.name")}
              value={tag.name}
              onSubmit={onChangeName}
            />
          </Grid>

          {/** Comments */}
          <Grid item xs={12}>
            <EditableTextField
              title={t("tags.comments")}
              value={tag.comment}
              onSubmit={onChangeComment}
            />
          </Grid>

          {/** Modified date */}
          {<Grid item xs={12}>
            <TextWithLabel
              title={t("tags.date")}
              value={dateString}
            />
          </Grid>}

          {/** Tag Collection */}
          {<Grid item xs={12}>
            <EditableDropdown
              title={t('tags.collection')}
              value={asset.id}
              onSubmit={onChangeAsset}
              options={options}
            />
          </Grid>}
        </Grid>
      </DialogContent>}
    </React.Fragment>
  );
};

interface TagCreateDialogProps {
  open: boolean;
  onClose(): void;
  coordinate: number[];
}

export const TagCreateDialog = memo((props: TagCreateDialogProps) => {
  const {open, onClose, coordinate} = props;

  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const iconUpdateDialog = useDialog();
  const {viewer} = useViewer();

  const assets = useSelector(selectAllAssets);
  const tagFiles = getTagAssets(assets);
  const folders = useSelector(selectAllFolders);

  const [title, setTitle] = useState("");
  const [comment, setComment] = useState("");
  const [collectionID, setCollectionID] = useState("");
  const [collectionName, setCollectionName] = useState("");
  const [folderID, setFolderID] = useState(NEW_FOLDER_ID);
  const [tagTexture, setTagTexture] = useState(defaultTagTexture);
  const [tagSize, setTagSize] = useState(TagSize.Medium);

  const onSubmit = async () => {
    onClose();

    const newTag = {
      id: nanoid(),
      name: title,
      comment: comment,
      date: new Date().toISOString(),
      x: coordinate[0],
      y: coordinate[1],
      annotations: []
    } as TagItemData;

    if (coordinate.length === 3) {
      newTag.z = coordinate[2];
      newTag.sceneState = viewer.sceneState;
    }

    let tagFolderID;

    if (createNewFolder) {
      // Create new folder
      const folder = await dispatch(createFolder());
      tagFolderID = folder.id;
    } else {
      tagFolderID = folderID;
    }

    let tagCollectionID;

    if (createNewFile) {
      // Create new tag collection
      const asset = await dispatch(createAsset({
        folderID: tagFolderID,
        name: collectionName,
        type: AssetType.Tag,
        texture: tagTexture,
        size: tagSize
      }));

      tagCollectionID = asset.id;
    } else {
      tagCollectionID = collectionID;
    }

    // Add new tag to collection
    await dispatch(createTags({
      assetID: tagCollectionID,
      tags: [newTag]
    }));

    toast.success(t("toast.tag-added"));
  };

  const resetDefaultValues = () => {
    setTitle("");
    setComment("");
    setCollectionName("");
    setTagTexture(defaultTagTexture);

    iconUpdateDialog.handleClose();

    if (hasExistingFolders) {
      setFolderID(folders[0].id);
    } else {
      setFolderID(NEW_FOLDER_ID);
    }

    if (hasExistingCSVs) {
      setCollectionID(tagFiles[0].id);
    } else {
      setCollectionID("");
    }
  };

  const submitTagIcon = useCallback((texture: string, size: number) => {
    setTagTexture(texture);
    setTagSize(size);
    iconUpdateDialog.handleClose();
  }, []);

  useEffect(() => {
    if (!open) return;
    resetDefaultValues();
  }, [open]);

  const hasExistingCSVs = tagFiles.length > 0;
  const hasExistingFolders = folders.length > 0;
  const createNewFile = collectionID === NEW_CSV_ID;
  const createNewFolder = folderID === NEW_FOLDER_ID;

  /* Determine valid state for new tag */
  let canSubmit = true;
  const titleMissing = title.trim() === "";
  const nameMissing = collectionName.trim() === "";
  canSubmit = canSubmit && !titleMissing;
  canSubmit = canSubmit && !(nameMissing && createNewFile);

  // Folder is used if it is user made or if it contains assets
  const folderOptions = folders.filter(folder => {
    const items = assets.filter(x => x.folderID === folder.id);
    return folder.default ? items.length > 0 : true;
  });

  return (
    <React.Fragment>
      {/* New tag dialog */}
      <SimpleDialog
        open={open && !iconUpdateDialog.open}
        onClose={onClose}
        onSubmit={onSubmit}
        canSubmit={canSubmit}
        maxWidth={"xs"}
        title={t("tags.new-title")}
        button={t('buttons.submit')}
      >
        <Grid container spacing={2}>

          {/** Title */}
          <Grid item xs={12}>
            <InputWithLabel
              title={t("tags.name")}
              value={title}
              onChange={setTitle}
            />
          </Grid>

          {/** Comments */}
          <Grid item xs={12}>
            <InputWithLabel
              multiline
              required={false}
              title={t("tags.comments")}
              value={comment}
              onChange={setComment}
            />
          </Grid>

          <Grid item xs={12}>
            <DropdownWithLabel
              title={t('tags.collection')}
              value={collectionID}
              onChange={setCollectionID}
              seperator={[0]}
              options={[
                [NEW_CSV_ID, t('tags.create_new_collection')],
                ...tagFiles.map(tagCSV => [tagCSV.id, tagCSV.name])
              ] as any}
            />
          </Grid>

          <div style={{
            width: "100%",
            padding: theme.spacing(0, 1)
          }}>
            <Collapse in={createNewFile}>
              <DividerWithText
                label={t('tags.new_collection_details')}
              />

              {/** Tag collection name */}
              <Grid item xs={12}>
                <InputWithLabel
                  title={t('tags.collection_name')}
                  value={collectionName}
                  onChange={setCollectionName}
                />
              </Grid>

              {/** Tag collection folder */}
              <Grid item xs={12}>
                <DropdownWithLabel
                  title={t('tags.collection_folder')}
                  value={folderID}
                  onChange={setFolderID}
                  seperator={[0]}
                  options={[
                    [NEW_FOLDER_ID, t("tags.new-folder")],
                    ...folderOptions.map(folder => {
                      const localizedFolderName = getLocalizedFolderName(folder.name);
                      return [folder.id, localizedFolderName];
                    })
                  ]}
                />
              </Grid>

              {/** Default Icon */}
              <Grid item xs={12}>
                <div className={clsx(classes.avatarParent, classes.textField)}>
                  <InputLabel>
                    {t('tags.collection_icon')}
                  </InputLabel>
                  <Avatar
                    src={getTagTexturePath(tagTexture)}
                    onClick={iconUpdateDialog.handleOpen}
                    className={classes.tagAvatar}
                  />
                </div>
              </Grid>
            </Collapse>
          </div>

        </Grid>
      </SimpleDialog>

      {/* Tag Icon */}
      <TagIconDialog
        open={iconUpdateDialog.open}
        currentTexture={tagTexture}
        currentSize={tagSize}
        onSubmit={submitTagIcon}
        onClose={iconUpdateDialog.handleClose}
      />
    </React.Fragment>
  );
});

export const showTagDetails = (tagID: string) => {
  sendCustomEvent("tag-details", tagID);
};
